import { useNavigate, useParams } from 'react-router-dom'
import {
  useCallback, useEffect, useMemo, useState,
} from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { usePromisifyDispatch } from '@Infowijs-eng/component-library/modules'
import ModalWithSteps from '../ModalWithSteps'
import ExporterMagisterSetup from './ExporterMagisterSetup'
import ExporterLASSetup from './ExporterLASSetup'
import ExporterSomtodaySetup from './ExporterSomtodaySetup'
import { OrganisationalUnit, PreferredLAS } from '../../types'
import ExporterOrganisationalUnitSelect from './ExporterOrganisationalUnitSelect'
import makeGetCustomerProductOrganisationId
  from '../../selectors/customerProducts/makeGetCustomerProductExporterOrganisationId'
import getCustomerProductName from '../../selectors/customerProducts/getCustomerProductName'
import getCustomerProductLAS from '../../selectors/customerProducts/getCustomerProductLAS'
import updateCustomerProduct from '../../actions/customerProducts/updateCustomerProduct'
import fetchCustomerProduct from '../../actions/customerProducts/fetchCustomerProduct'

export default function ExporterCreateModal() {
  const totalSteps = 1
  const currentStep = 1
  const [selectedLas, setSelectedLas] = useState<PreferredLAS | undefined>()
  const [selectedOrganisation, setSelectedOrganisation] = useState<string | undefined>()
  const [selectedOrganisationalUnits, setSelectedOrganisationalUnits] = useState<OrganisationalUnit[]>([])
  const dispatch = useDispatch()

  const getCustomerProductExporterOrganisationId = useMemo(makeGetCustomerProductOrganisationId, [])

  const { customerProductId, mode } = useParams()
  const promisifyDispatch = usePromisifyDispatch()

  const customerProductName = useSelector((state) => getCustomerProductName(state, { customerProductId }))
  const customerProductLAS = useSelector((state) => getCustomerProductLAS(state, { customerProductId }))
  const currentOrganisationId = useSelector(
    (state) => getCustomerProductExporterOrganisationId(state, { customerProductId }),
  )

  const isEditing = mode === 'edit'

  useEffect(() => {
    setSelectedLas(customerProductLAS)
  }, [customerProductLAS])

  const alertMessage = 'Are you sure you want to close this modal?'
  const navigate = useNavigate()

  const onUpdateCustomerProduct = useCallback(() => {
    promisifyDispatch(updateCustomerProduct({
      id: customerProductId,
      metadata: {
        las: selectedLas,
        exporters: {
          somtoday: selectedLas === 'somtoday' ? {
            organisationId: selectedOrganisation,
            organisationalUnits: selectedOrganisationalUnits.map((org) => ({
              id: org.id,
              name: org.name,
            })),
          } : null,
          magister: selectedLas === 'magister' ? {
            organisationId: selectedOrganisation,
            organisationalUnits: selectedOrganisationalUnits.map((org) => ({
              id: org.id,
              name: org.name,
            })),
          } : null,
        },
      },
    })).then(() => {
      dispatch(fetchCustomerProduct(customerProductId))
      navigate(`/customers/products/${customerProductId}`, { replace: true })
    })
  }, [selectedLas, selectedOrganisation, selectedOrganisationalUnits, customerProductId])

  return (
    <ModalWithSteps
      title={isEditing ? 'Update Exporter' : 'Add Exporter'}
      currentStep={currentStep}
      // eslint-disable-next-line no-alert
      onClose={() => {
        // eslint-disable-next-line no-alert
        if (window.confirm(alertMessage)) {
          navigate(`/customers/products/${customerProductId}`, { replace: true })
        }
      }}
      nextButtonProps={{
        disabled: !selectedLas || !selectedOrganisation || selectedOrganisationalUnits?.length < 1,
        onClick: () => {
          onUpdateCustomerProduct()
        },
        children: isEditing ? 'Update' : 'Create',
      }}
      totalSteps={totalSteps}
    >
      <div className="flex flex-col h-full w-full gap-8">
        <ExporterLASSetup
          onSelectLAS={(las) => {
            setSelectedOrganisation(undefined)
            setSelectedOrganisationalUnits([])
            setSelectedLas(las)
          }}
          activeLAS={selectedLas}
        />
        {selectedLas === 'somtoday' && (
          <ExporterSomtodaySetup
            currentOrganisationId={currentOrganisationId}
            onSelectedOrganisation={(id) => {
              setSelectedOrganisation(id)
              if (id === undefined) {
                setSelectedOrganisationalUnits([])
              }
            }}
          />
        )}
        {selectedLas === 'magister' && (
          <ExporterMagisterSetup
            customerProductName={customerProductName}
            currentOrganisationId={currentOrganisationId}
            onSelectedOrganisation={(id) => {
              setSelectedOrganisation(id)
              if (id === undefined) {
                setSelectedOrganisationalUnits([])
              }
            }}
          />
        )}

        {selectedLas && selectedOrganisation && (
          <ExporterOrganisationalUnitSelect
            customerProductName={customerProductName}
            las={selectedLas}
            organisationId={selectedOrganisation}
            onUpdateSelectedOrganisationalUnits={(data) => {
              setSelectedOrganisationalUnits(data)
            }}
          />
        )}
      </div>
    </ModalWithSteps>
  )
}
